/*--------------------------------------------------------------------------------------------
Program     :  CalculateTrvlTime.js
Description :  Main program to calculate the traveltime with the modified payload after, 
               payload compare. 
Date        :  30/10/2025 
Created By  :  Jelbin
---------------------------------------------------------------------------------------------
TicketNo        Author          Date            ChangeDesc
---------------------------------------------------------------------------------------------
30102025       -Jelbin     -  30/10/2025         Created
---------------------------------------------------------------------------------------------*/

// -------------------------------------------------------------------------------------------
// Main Logic Overview:
// -------------------------------------------------------------------------------------------
// After comparing the base and modified payloads in `PayloadCompare.js`,
// the resulting merged payload (`comparedPayload`) is passed back to
// `Rightsidebar.js`, where it is assigned using `comparedPayload`.
//
// This program is invoked from `Rightsidebar.js` and uses the
// latest combined payload (`comparedPayload`) as its input parameter and start to recalculate the traveltime.
// -------------------------------------------------------------------------------------------

import React, { useEffect } from "react";
import TimeCalculationService from '../service/TimeCalculationService';
import { useMapTrackInfo } from "../context/Maptrackcontext";



const startTravlTimeCalculation = async (InputcomparedPayload, durationFieldsY,
    tskTravelTimeField, selectedtraveler, ZDATERANGE,
    timeATPOI, selectedSourceMapname, startTimeField,
    endTimeField, fixschedulefield, fielduserstartTime,
    participantsData, mergeparticipantFields, setParticipantTaskTime) => {


    /********* function call the recalculateTaskTimes  *************************/
    const handleAdjustTraveltime = (userblock, datevalue) => {

        // Use the latest tasks from the updated user object
        const latestTasksForDate = userblock.details.filter(
            d => d[ZDATERANGE].slice(0, 10) === datevalue);

        // find user day start time
        const userdaystartTime = userblock[fielduserstartTime];

        // After the travle time calculation adjust the task StartTime and EndTime
        const destTasksWithNewTimes = TimeCalculationService.recalculateTaskTimes(
            latestTasksForDate,
            startTimeField,
            endTimeField,
            durationFieldsY,
            userdaystartTime,
            tskTravelTimeField,
            fixschedulefield,
            datevalue,
            ZDATERANGE
        );

        // Update the main payload with the tasks that now have adjusted start/end times
        const agentIndex = InputcomparedPayload.findIndex(u => u[selectedtraveler] === userblock[selectedtraveler]);
        if (agentIndex !== -1) {
            // InputcomparedPayload[agentIndex].details = destTasksWithNewTimes;
            const originalTasks = InputcomparedPayload[agentIndex].details;
            // Filter out tasks for the current date
            const dateTasks = originalTasks.filter(d => d[ZDATERANGE].slice(0, 10) !== datevalue);
            // Merge updated tasks for this date
            InputcomparedPayload[agentIndex].details = [...dateTasks, ...destTasksWithNewTimes];
        }
    }

    /****MAIN LOGIC start for travel time recalculation logic ****/
    /*************************************************************/
    for (let user of InputcomparedPayload) {
        // Only process users where recalculation is required
        if (user.RECALCULATETRVLTIME !== "Y") continue;

        // Get all unique dates where tasks changed
        const modifiedTaskDates = [
            ...new Set(
                (user.details || [])
                    .filter(d => d.ZTRAVELTIMECALC === "Y")
                    .map(d => d[ZDATERANGE].slice(0, 10))
            ),
        ];

        // Find the date from user.POIChangedDates block whcih is not exist in modifiedTaskDates
        // That means on that dates only the USER POI gets modified(added/deleted)

        /***** Step:1  If user-level POI changes exist **************/
        if (user.USERPOICHANGED && Array.isArray(user.POIChangedDates)) {

            //Find POI-changed objects not already in modifiedTaskDates (keep full objects)
            const poiOnlyChangedDates = user.POIChangedDates.filter(p => !modifiedTaskDates.includes(p.POIDate));
            if (poiOnlyChangedDates.length > 0) {
                // Loop each POI-only date and recalculate final travel time.
                // Only recalculate the final travel time. No changes in the task level
                for (const poiChange of poiOnlyChangedDates) {
                    const { POIDate, POIUNKData } = poiChange;
                    const finalCalculatedData = await TimeCalculationService.calculateAgentFinaltraveltime(
                        user,
                        durationFieldsY,
                        "",
                        tskTravelTimeField,
                        user.finalTravelTime,
                        selectedtraveler,
                        POIDate,
                        ZDATERANGE,
                        timeATPOI,
                        POIUNKData,
                        selectedSourceMapname
                    );

                    /***Update each finalCalculatedData block back to InputcomparedPayload***/
                    //so later lopp uses the new data
                    if (finalCalculatedData[selectedtraveler]) {
                        //Find the matching user in the main payload and replace it
                        const userIndex = InputcomparedPayload.findIndex(
                            u => u[selectedtraveler] === finalCalculatedData[selectedtraveler]
                        );
                        if (userIndex !== -1) {
                            // Merge or replace — full replace is fine since service returns the whole user block
                            InputcomparedPayload[userIndex] = finalCalculatedData;
                            // Also refresh the current reference (so later code uses the new data)
                            // This refresh is important
                            user = InputcomparedPayload[userIndex];
                        } else {
                            console.warn(
                                `User ${finalCalculatedData[selectedtraveler]} not found in InputcomparedPayload`
                            );
                        }
                    }
                }
            }
        }

        /************ Step:2  If any task level changes exists **************/
        // Loop throgh each dates and get the taskfor each dates
        // Only when any task level changes exists/any POI added in task
        for (const datevalue of modifiedTaskDates) {
            const tasksForDate = user.details.filter(detail =>
                detail[ZDATERANGE].slice(0, 10) === datevalue
            );

            if (tasksForDate.length !== 0) {
                const taskRecalculateWithPOI = await TimeCalculationService.calculatetravelTimeAfterPayloadCompare(
                    user, tasksForDate, durationFieldsY,
                    tskTravelTimeField, user.finalTravelTime,
                    selectedtraveler, datevalue, ZDATERANGE,
                    timeATPOI, selectedSourceMapname);

                // Update main payload with the latest user object
                if (taskRecalculateWithPOI[selectedtraveler]) {
                    const userIndex = InputcomparedPayload.findIndex(
                        u => u[selectedtraveler] === taskRecalculateWithPOI[selectedtraveler]
                    );
                    if (userIndex !== -1) {
                        InputcomparedPayload[userIndex] = taskRecalculateWithPOI;
                        // refresh local reference so subsequent steps use the latest user
                        user = InputcomparedPayload[userIndex];
                    }
                }

                /* Step 2.2 Adjust the  task StartTime and EndTime after task trvtime calculation **/
                handleAdjustTraveltime(user, datevalue);
            }

        }

        /************ Step:3  If any changes for participants **************/
        // This block will execute when any participants change. 
        // If only the participant change , the above blocks(strep1,step2) will not execute. 
        // Only recalcuate the particpant travel time.

        // Get all unique dates where participants changed
        const modifiedPartiDates = [
            ...new Set(
                (user.details || [])
                    .filter(d => d.ZPARTITRAVELTIMECALC === "Y")
                    .map(d => d[ZDATERANGE].slice(0, 10))
            ),
        ];
        for (const partidatevalue of modifiedPartiDates) {

            // step : 3.1 Recalcualte the participant task duration
            const updatedAgentwithduration = await TimeCalculationService.calculatetaskdurationwithparticipants(
                user, durationFieldsY,
                participantsData, selectedtraveler,
                partidatevalue, ZDATERANGE,
                mergeparticipantFields, true);

            // step : 3.2 participant travel time recalculations
            // Pass the agent record with task duration
            const finalDatawithPartTravelTime = await TimeCalculationService.calculateParticipantSchedule(
                updatedAgentwithduration,
                tskTravelTimeField,
                selectedtraveler,
                partidatevalue,
                ZDATERANGE,
                participantsData,
                mergeparticipantFields,
                false,
                true,
                selectedSourceMapname
            );

            // Update back to main InputcomparedPayload
            if (finalDatawithPartTravelTime && finalDatawithPartTravelTime[selectedtraveler]) {
                const agentIndex = InputcomparedPayload.findIndex(
                    a => a[selectedtraveler] === finalDatawithPartTravelTime[selectedtraveler]);

                if (agentIndex !== -1) {
                    // Replace the full agent block, or merge just details if needed
                    InputcomparedPayload[agentIndex] = finalDatawithPartTravelTime;
                    user = InputcomparedPayload[agentIndex]; // keep reference current
                }
            }
            /* Step 3.3 Adjust the  task StartTime and EndTime after task trvtime calculation **/
            handleAdjustTraveltime(user, partidatevalue);
        }
    }

    /********* Step:4  After all calculation ********/
    //Call the function participantTotalTaskTime and it will create new payload 
    //with each partcipant Total Task time. 
    const FinalPartiTaskTime = await TimeCalculationService.participantTotalTaskTime(
        selectedtraveler, ZDATERANGE, participantsData,
        mergeparticipantFields, InputcomparedPayload, durationFieldsY);

    setParticipantTaskTime(FinalPartiTaskTime);
};

// ------------------------------
// Main component Area
// ------------------------------
const CalculateTrvlTime = ({ comparedPayload, handleResultTraveltimeCalc, durationFieldsY,
    tskTravelTimeField, selectedtraveler, ZDATERANGE, timeATPOI, selectedSourceMapname,
    startTimeField, endTimeField, fixschedulefield, fielduserstartTime, participantsData,
    mergeparticipantFields }) => {
    const { participantTaskTime, setParticipantTaskTime } = useMapTrackInfo();
    // Reset back all the flags to "N" after travel time recalculation
    const resetTravelFlags = (comparedPayload) => {
        // Make sure the payload exists and is an array
        if (!Array.isArray(comparedPayload)) return [];
        
        //Deep copy 
        const comparedPayloadCopy = structuredClone(comparedPayload);

        // Deep-clone and reset flags safely
        return comparedPayloadCopy.map(user => ({
            ...user,
            USERPOICHANGED: "N",
            RECALCULATETRVLTIME: "N",
            // For each task/detail under the user
            details: (user.details || []).map(detail => ({
                ...detail,
                ZTRAVELTIMECALC: "N",
                ZPARTITRAVELTIMECALC: "N"
            }))
        }));
    };
    
    /**********Main Blcok-Program starts from here*********/
    useEffect(() => {
        if (!comparedPayload?.length) return;

        const runTravelCalculation = async () => {
            
            // Function to calculate the travel time
            await startTravlTimeCalculation(
                comparedPayload,
                durationFieldsY,
                tskTravelTimeField,
                selectedtraveler,
                ZDATERANGE,
                timeATPOI,
                selectedSourceMapname,
                startTimeField, endTimeField,
                fixschedulefield, fielduserstartTime,
                participantsData, mergeparticipantFields,
                setParticipantTaskTime
            );

            //Reset back all the flags to "N" after travel time recalculation
            const comparedPayloadAfterFlagReset = resetTravelFlags(comparedPayload);
            if (handleResultTraveltimeCalc) handleResultTraveltimeCalc(comparedPayloadAfterFlagReset);
        };

        runTravelCalculation();
    }, [
        comparedPayload,
        handleResultTraveltimeCalc,
        durationFieldsY,
        tskTravelTimeField,
        selectedtraveler,
        ZDATERANGE,
        timeATPOI,
        selectedSourceMapname,
        startTimeField,
        endTimeField, fixschedulefield, fielduserstartTime
    ]);

    return null; // no UI — processing only
};

export default CalculateTrvlTime;
